import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslations } from "adapters/i18n";
import React from "react";
import {
  AVG_DAILY_GAIN_TABLE_COLUMN,
  getAvgDailyGainTableColumns,
} from "../arrobaBalanceUtil";

const ArrobaBalanceAvgDailyGainTable = ({ classes, farmBalances }) => {
  const { t } = useTranslations();

  const tableColumns = getAvgDailyGainTableColumns(t);

  return (
    <Grid>
      <Typography className={classes.title}>
        {t("arrobaBalance:avg-daily-gain-summary")}
      </Typography>
      <Paper className={classes.tableContainer}>
        <Table id="avg-daily-gain-table" className={classes.table}>
          <TableHead
            data-testid="avg-daily-gain-table-header"
            className={classes.tableHeader}
          >
            <TableRow>
              {tableColumns.map((column) => (
                <TableHeaderCell
                  key={column.id}
                  classes={classes}
                  columnId={column.id}
                  label={column.label}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody data-testid="avg-daily-gain-table-body">
            <TableRow>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.RebanhoMedio}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.ProducaoEmQuilos}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.ProducaoEmArrobas}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Periodo}
              </TableCell>
              <TableCell className={classes.tableRowAvgDailyGainCell}>
                {farmBalances.GMD}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Grid>
  );
};

const TableHeaderCell = ({ classes, columnId, label }) => {
  const { t } = useTranslations();

  const kg = t("common:measure.kg");
  const arroba = t("common:measure.arroba");

  const tooltipTextMap = {
    [AVG_DAILY_GAIN_TABLE_COLUMN.AVG_HERD_SIZE]: t(
      "livestock:average-herd-disclaimer"
    ),
    [AVG_DAILY_GAIN_TABLE_COLUMN.PROD_PER_CATTLE_KG]: t(
      "arrobaBalance:avg-cattle-production-in-x",
      { unit: kg }
    ),
    [AVG_DAILY_GAIN_TABLE_COLUMN.PROD_PER_CATTLE_ARROBA]: t(
      "arrobaBalance:avg-cattle-production-in-x",
      { unit: arroba }
    ),
    [AVG_DAILY_GAIN_TABLE_COLUMN.AVG_DAILY_GAIN]: t(
      "arrobaBalance:avg-daily-gain-disclaimer"
    ),
  };

  const cellClassesMap = {
    [AVG_DAILY_GAIN_TABLE_COLUMN.AVG_HERD_SIZE]: classes.headerFirstCell,
    [AVG_DAILY_GAIN_TABLE_COLUMN.PROD_PER_CATTLE_KG]: classes.headerCell,
    [AVG_DAILY_GAIN_TABLE_COLUMN.PROD_PER_CATTLE_ARROBA]: classes.headerCell,
    [AVG_DAILY_GAIN_TABLE_COLUMN.TIMEFRAME]: classes.headerCell,
    [AVG_DAILY_GAIN_TABLE_COLUMN.AVG_DAILY_GAIN]:
      classes.avgDailyGainHeaderCell,
  };

  const tooltipText = tooltipTextMap[columnId];
  const cellClass = cellClassesMap[columnId];

  const tooltipIconColor =
    columnId === AVG_DAILY_GAIN_TABLE_COLUMN.AVG_DAILY_GAIN
      ? "inherit"
      : "primary";

  return (
    <TableCell className={cellClass}>
      <div className={classes.headerCellContent}>
        {label}
        {tooltipText != null && (
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            title={tooltipText}
            placement="top"
          >
            <InfoIcon
              color={tooltipIconColor}
              className={classes.tooltipInfoIcon}
              placement="top"
            />
          </Tooltip>
        )}
      </div>
    </TableCell>
  );
};

const styles = (theme) => {
  const defaultHeaderCell = {
    fontSize: "14px",
    fontWeight: "500",
    letterSpacing: "1px",
    padding: "4px 24px 4px 24px",
    textAlign: "center",
  };

  const defaultRowCell = {
    fontSize: "18px",
    fontWeight: "500",
    lineHeight: "normal",
    padding: "4px 24px 4px 24px",
    textAlign: "center",
  };

  return {
    title: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
      marginBottom: "20px",
    },
    tableContainer: {
      borderRadius: "16px",
    },
    table: {
      fontFamily: "Roboto",
      textAlign: "center",
      borderRadius: "16px",
      padding: "4px 24px 4px 24px",
      fontSize: "14px",
      width: "138,500px",
      height: "1472,470px",
    },
    tableHeader: {
      padding: "4px 24px 4px 24px",
      marginTop: "25px",
      marginBottom: "25px",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      backgroundColor: "#e1e1e1",
      borderRadius: "16px",
    },
    headerFirstCell: {
      ...defaultHeaderCell,
      borderRadius: "16px 0 0 0",
      color: theme.palette.primary.main,
    },
    headerCell: {
      ...defaultHeaderCell,
      color: theme.palette.primary.main,
    },
    avgDailyGainHeaderCell: {
      ...defaultHeaderCell,
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0 16px 0 0",
      color: "#fff",
    },
    headerCellContent: {
      display: "flex",
      justifyContent: "center",
      height: "inherit",
      alignItems: "center",
    },
    tooltip: {
      fontSize: "10px",
      padding: "8px",
      borderRadius: "4px",
      color: "#ffffff",
      backgroundColor: "#000000BF",
    },
    tooltipInfoIcon: {
      fontSize: "14px",
      position: "relative",
    },
    tableRowCell: {
      ...defaultRowCell,
      borderBottom: "none",
      borderRight: "1px solid #e5e5ea",
      color: "#666",
    },
    tableRowAvgDailyGainCell: {
      ...defaultRowCell,
      borderLeft: "1px solid #e5e5ea",
      borderRadius: "0 0 0 16px",
      color: theme.palette.primary.main,
    },
  };
};

export default withStyles(styles)(ArrobaBalanceAvgDailyGainTable);
